import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "@/components/ui/dialog";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
	Coins,
	ArrowUpRight,
	ArrowDownRight,
	Briefcase,
	Headphones,
	Activity,
	Crown,
	Upload,
	Check,
	X,
	Infinity as InfinityIcon,
	MessageSquare,
	Archive,
	Share2,
} from "lucide-react";
import type { UserDetails } from "@/lib/platformManager/platformManager.service";
import { useTranslation } from "react-i18next";

interface PlatformManagerUserDetailsProps {
	userDetails: UserDetails;
	userDetailsModalOpen: boolean;
	setUserDetailsModalOpen: (open: boolean) => void;
	loadingUserDetails: boolean;
}

export const PlatformManagerUserDetailsComponent = ({
	userDetails,
	userDetailsModalOpen,
	setUserDetailsModalOpen,
	loadingUserDetails,
}: PlatformManagerUserDetailsProps) => {
	const { t } = useTranslation();
	// Format date helper
	const formatDate = (dateString: string | null) => {
		if (!dateString) return t("common.never");
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	return (
		<>
			{/* User Details Modal */}
			<Dialog
				open={userDetailsModalOpen}
				onOpenChange={setUserDetailsModalOpen}
			>
				<DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto sm:max-w-6xl">
					<DialogHeader>
						<DialogTitle className="text-2xl">
							{t("platformManager.userDetails.title")}
						</DialogTitle>
						<DialogDescription>
							{t("platformManager.userDetails.description")}
						</DialogDescription>
					</DialogHeader>

					{loadingUserDetails ? (
						<div className="flex items-center justify-center py-8">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
						</div>
					) : userDetails ? (
						<div className="space-y-6">
							{/* User Info Card */}
							<Card>
								<CardHeader>
									<CardTitle className="text-lg">
										{t("platformManager.userDetails.userInfo")}
									</CardTitle>
								</CardHeader>
								<CardContent className="space-y-3">
									<div className="grid grid-cols-2 gap-4">
										<div>
											<p className="text-sm text-muted-foreground">
												{t("common.email")}
											</p>
											<p className="font-medium">{userDetails.user.email}</p>
										</div>
										<div>
											<p className="text-sm text-muted-foreground">
												{t("common.name")}
											</p>
											<p className="font-medium">
												{userDetails.user.name || "-"}
											</p>
										</div>
										<div>
											<p className="text-sm text-muted-foreground">
												{t("platformManager.userDetails.joined")}
											</p>
											<p className="font-medium">
												{formatDate(userDetails.user.createdAt)}
											</p>
										</div>
										<div>
											<p className="text-sm text-muted-foreground">
												{t("platformManager.userDetails.lastActive")}
											</p>
											<p className="font-medium">
												{formatDate(userDetails.user.lastActive)}
											</p>
										</div>
									</div>
								</CardContent>
							</Card>

							{/* Subscription Card */}
							<Card className="bg-gradient-to-br from-purple-500/5 via-purple-500/0 to-purple-500/5 border-purple-500/20">
								<CardHeader>
									<CardTitle className="text-lg flex items-center gap-2">
										<Crown className="h-5 w-5 text-purple-500" />
										{t("platformManager.userDetails.subscription")}
									</CardTitle>
								</CardHeader>
								<CardContent className="space-y-4">
									<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
										<div>
											<p className="text-sm text-muted-foreground">
												{t("platformManager.userDetails.plan")}
											</p>
											<p className="font-bold text-purple-600">
												{userDetails.subscription.planName}
											</p>
										</div>
										<div>
											<p className="text-sm text-muted-foreground">
												{t("platformManager.userDetails.status")}
											</p>
											<p className="font-medium">
												{userDetails.subscription.status}
											</p>
										</div>
										{userDetails.subscription.endsAt && (
											<div>
												<p className="text-sm text-muted-foreground">
													{t("platformManager.userDetails.endsAt")}
												</p>
												<p className="font-medium">
													{formatDate(userDetails.subscription.endsAt)}
												</p>
											</div>
										)}
									</div>

									{/* Limits Section */}
									<div>
										<p className="text-sm font-semibold text-muted-foreground mb-2">
											{t("platformManager.userDetails.limits")}
										</p>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
											{/* Monthly Coins */}
											{userDetails.subscription.limits.maxUploadMb !==
												undefined && (
												<div className="flex items-center gap-2 p-2 rounded-lg bg-card border">
													<Upload className="h-4 w-4 text-blue-500" />
													<div className="flex-1">
														<p className="text-xs text-muted-foreground">
															{t("platformManager.userDetails.maxUpload")}
														</p>
														<p className="font-semibold">
															{userDetails.subscription.limits.maxUploadMb} MB
														</p>
													</div>
												</div>
											)}

											{/* Monthly Coins */}
											{userDetails.subscription.limits.monthlyCoins !==
												undefined && (
												<div className="flex items-center gap-2 p-2 rounded-lg bg-card border">
													<Coins className="h-4 w-4 text-amber-500" />
													<div className="flex-1">
														<p className="text-xs text-muted-foreground">
															{t("platformManager.userDetails.monthlyCoins")}
														</p>
														<p className="font-semibold">
															{userDetails.subscription.limits.monthlyCoins ===
															-1 ? (
																<span className="flex items-center gap-1">
																	<InfinityIcon className="h-4 w-4" />
																	{t("platformManager.userDetails.unlimited")}
																</span>
															) : (
																userDetails.subscription.limits.monthlyCoins
															)}
														</p>
													</div>
												</div>
											)}

											{/* Max Chats */}
											{userDetails.subscription.limits.maxChats !==
												undefined && (
												<div className="flex items-center gap-2 p-2 rounded-lg bg-card border">
													<MessageSquare className="h-4 w-4 text-blue-500" />
													<div className="flex-1">
														<p className="text-xs text-muted-foreground">
															{t("platformManager.userDetails.maxChats")}
														</p>
														<p className="font-semibold">
															{userDetails.subscription.limits.maxChats ===
															-1 ? (
																<span className="flex items-center gap-1">
																	<InfinityIcon className="h-4 w-4" />
																	{t("platformManager.userDetails.unlimited")}
																</span>
															) : (
																userDetails.subscription.limits.maxChats
															)}
														</p>
													</div>
												</div>
											)}

											{/* Max Storage Episodes */}
											{userDetails.subscription.limits.maxStorageEpisodes !==
												undefined && (
												<div className="flex items-center gap-2 p-2 rounded-lg bg-card border">
													<Archive className="h-4 w-4 text-indigo-500" />
													<div className="flex-1">
														<p className="text-xs text-muted-foreground">
															{t(
																"platformManager.userDetails.maxStorageEpisodes",
															)}
														</p>
														<p className="font-semibold">
															{userDetails.subscription.limits
																.maxStorageEpisodes === -1 ? (
																<span className="flex items-center gap-1">
																	<InfinityIcon className="h-4 w-4" />
																	{t("platformManager.userDetails.unlimited")}
																</span>
															) : (
																userDetails.subscription.limits
																	.maxStorageEpisodes
															)}
														</p>
													</div>
												</div>
											)}

											{/* Features - Chapter Sharing */}
											{userDetails.subscription.limits.features
												?.chapterSharing !== undefined && (
												<div className="flex items-center gap-2 p-2 rounded-lg bg-card border">
													{userDetails.subscription.limits.features
														.chapterSharing ? (
														<Share2 className="h-4 w-4 text-green-500" />
													) : (
														<X className="h-4 w-4 text-red-500" />
													)}
													<div className="flex-1">
														<p className="text-xs text-muted-foreground">
															{t("platformManager.userDetails.chapterSharing")}
														</p>
														<p className="font-semibold">
															{userDetails.subscription.limits.features
																.chapterSharing
																? t("common.enabled")
																: t("common.disabled")}
														</p>
													</div>
												</div>
											)}

											{/* Features - Whole Podcast Sharing */}
											{userDetails.subscription.limits.features
												?.wholePodcastSharing !== undefined && (
												<div className="flex items-center gap-2 p-2 rounded-lg bg-card border">
													{userDetails.subscription.limits.features
														.wholePodcastSharing ? (
														<Share2 className="h-4 w-4 text-green-500" />
													) : (
														<X className="h-4 w-4 text-red-500" />
													)}
													<div className="flex-1">
														<p className="text-xs text-muted-foreground">
															{t(
																"platformManager.userDetails.wholePodcastSharing",
															)}
														</p>
														<p className="font-semibold">
															{userDetails.subscription.limits.features
																.wholePodcastSharing
																? t("common.enabled")
																: t("common.disabled")}
														</p>
													</div>
												</div>
											)}

											{/* Features - Batch Creation */}
											{userDetails.subscription.limits.features
												?.batchCreation !== undefined && (
												<div className="flex items-center gap-2 p-2 rounded-lg bg-card border">
													{userDetails.subscription.limits.features
														.batchCreation ? (
														<Check className="h-4 w-4 text-green-500" />
													) : (
														<X className="h-4 w-4 text-red-500" />
													)}
													<div className="flex-1">
														<p className="text-xs text-muted-foreground">
															{t("platformManager.userDetails.batchCreation")}
														</p>
														<p className="font-semibold">
															{userDetails.subscription.limits.features
																.batchCreation
																? t("common.enabled")
																: t("common.disabled")}
														</p>
													</div>
												</div>
											)}

											{/* Features - Retain Access */}
											{userDetails.subscription.limits.features
												?.retainAccess !== undefined && (
												<div className="flex items-center gap-2 p-2 rounded-lg bg-card border">
													{userDetails.subscription.limits.features
														.retainAccess ? (
														<Check className="h-4 w-4 text-green-500" />
													) : (
														<X className="h-4 w-4 text-red-500" />
													)}
													<div className="flex-1">
														<p className="text-xs text-muted-foreground">
															{t("platformManager.userDetails.retainAccess")}
														</p>
														<p className="font-semibold">
															{userDetails.subscription.limits.features
																.retainAccess
																? t("common.enabled")
																: t("common.disabled")}
														</p>
													</div>
												</div>
											)}
										</div>
									</div>
								</CardContent>
							</Card>

							{/* Statistics Cards */}
							<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
								<Card>
									<CardContent className="pt-6">
										<div className="flex items-center gap-3">
											<div className="p-2 rounded-lg bg-gradient-to-br from-orange-500/20 to-orange-600/10">
												<Coins className="h-5 w-5 text-orange-500" />
											</div>
											<div>
												<p className="text-xs text-muted-foreground">
													{t("platformManager.userDetails.currentBalance")}
												</p>
												<p className="text-xl font-bold">
													{userDetails.balance.toLocaleString()}
												</p>
											</div>
										</div>
									</CardContent>
								</Card>

								<Card>
									<CardContent className="pt-6">
										<div className="flex items-center gap-3">
											<div className="p-2 rounded-lg bg-gradient-to-br from-green-500/20 to-green-600/10">
												<ArrowUpRight className="h-5 w-5 text-green-500" />
											</div>
											<div>
												<p className="text-xs text-muted-foreground">
													{t("platformManager.userDetails.totalEarned")}
												</p>
												<p className="text-xl font-bold">
													{userDetails.stats.totalEarned.toLocaleString()}
												</p>
											</div>
										</div>
									</CardContent>
								</Card>

								<Card>
									<CardContent className="pt-6">
										<div className="flex items-center gap-3">
											<div className="p-2 rounded-lg bg-gradient-to-br from-red-500/20 to-red-600/10">
												<ArrowDownRight className="h-5 w-5 text-red-500" />
											</div>
											<div>
												<p className="text-xs text-muted-foreground">
													{t("platformManager.userDetails.totalSpent")}
												</p>
												<p className="text-xl font-bold">
													{userDetails.stats.totalSpent.toLocaleString()}
												</p>
											</div>
										</div>
									</CardContent>
								</Card>

								<Card>
									<CardContent className="pt-6">
										<div className="flex items-center gap-3">
											<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/10">
												<Headphones className="h-5 w-5 text-blue-500" />
											</div>
											<div>
												<p className="text-xs text-muted-foreground">
													{t("platformManager.userDetails.podcasts")}
												</p>
												<p className="text-xl font-bold">
													{userDetails.stats.podcastCount}
												</p>
											</div>
										</div>
									</CardContent>
								</Card>

								<Card>
									<CardContent className="pt-6">
										<div className="flex items-center gap-3">
											<div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-purple-600/10">
												<Briefcase className="h-5 w-5 text-purple-500" />
											</div>
											<div>
												<p className="text-xs text-muted-foreground">
													{t("platformManager.userDetails.workspaces")}
												</p>
												<p className="text-xl font-bold">
													{userDetails.stats.workspaceCount}
												</p>
											</div>
										</div>
									</CardContent>
								</Card>

								<Card>
									<CardContent className="pt-6">
										<div className="flex items-center gap-3">
											<div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500/20 to-indigo-600/10">
												<Activity className="h-5 w-5 text-indigo-500" />
											</div>
											<div>
												<p className="text-xs text-muted-foreground">
													{t("platformManager.userDetails.transactions")}
												</p>
												<p className="text-xl font-bold">
													{userDetails.stats.transactionCount}
												</p>
											</div>
										</div>
									</CardContent>
								</Card>
							</div>

							{/* Recent Transactions */}
							{userDetails.recentTransactions.length > 0 && (
								<Card>
									<CardHeader>
										<CardTitle className="text-lg">
											{t("platformManager.userDetails.recentTransactions")}
										</CardTitle>
									</CardHeader>
									<CardContent>
										<div className="space-y-2">
											{userDetails.recentTransactions.map(
												(transaction: any) => (
													<div
														key={transaction.id}
														className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
													>
														<div className="flex items-center gap-3">
															<div
																className={`p-2 rounded-lg ${
																	transaction.amount >= 0
																		? "bg-green-500/10"
																		: "bg-red-500/10"
																}`}
															>
																{transaction.amount >= 0 ? (
																	<ArrowUpRight className="h-4 w-4 text-green-500" />
																) : (
																	<ArrowDownRight className="h-4 w-4 text-red-500" />
																)}
															</div>
															<div>
																<p className="font-medium text-sm">
																	{transaction.description ||
																		transaction.type ||
																		t("common.transaction")}
																</p>
																<p className="text-xs text-muted-foreground">
																	{new Date(
																		transaction.createdAt,
																	).toLocaleDateString("en-US", {
																		year: "numeric",
																		month: "short",
																		day: "numeric",
																		hour: "2-digit",
																		minute: "2-digit",
																	})}
																</p>
															</div>
														</div>
														<div
															className={`font-bold ${
																transaction.amount >= 0
																	? "text-green-600"
																	: "text-red-600"
															}`}
														>
															{transaction.amount >= 0 ? "+" : ""}
															{transaction.amount.toLocaleString()}
														</div>
													</div>
												),
											)}
										</div>
									</CardContent>
								</Card>
							)}
						</div>
					) : null}

					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setUserDetailsModalOpen(false)}
						>
							{t("common.close")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};
